// F3 — la secuencia que va desde el botón "Buscar billete" hasta la URL de compra.
//
// No es un submit. En "Tren + Hotel / Ocio" el botón abre una CONVERSACIÓN, y lo que el
// cliente conteste decide a qué URL se va:
//
//   Buscar ─▶ "¿Quieres añadir hotel al viaje?"
//              ├─ Sí ─▶ [si es sólo ida] calendario de entrada y salida del HOTEL
//              │         └─▶ pasajeros (modal de tren+hotel, 2 adultos) ─▶ Compra/3833
//              └─ No ─▶ pasajeros (modal de tren+ocio, 1 adulto)
//                        └─▶ ¿hay paquete Viaje+ para ese destino?
//                             ├─ sí ─▶ CompraDestino/{idPaquete}
//                             └─ no ─▶ la forma _param… contra la landing del destino
//
// En "Tren + Hotel + Ocio" no hay conversación: el botón construye la forma _param… y se va.
//
// Los buscadores de UN SOLO PRODUCTO (`data-producto`, ver buscador.js) son este mismo bloque
// con la conversación cambiada:
//
//   tren-hotel   Buscar ─▶ [si es sólo ida] noches del hotel ─▶ pasajeros ─▶ Compra/3833
//                (la pregunta del hotel no se hace: ya está contestada que sí)
//   tren-coche   Buscar ─▶ "¿Quieres añadir hotel al viaje?"   (como la página de coches)
//                  ├─ Sí ─▶ [si es sólo ida] noches ─▶ pasajeros + conductor ─▶ Compra/5997
//                  └─ No ─▶ [si es sólo ida] recogida y devolución del coche
//                            └─▶ pasajeros + conductor ─▶ Compra/5996
//
// Que el modal de pasajeros sea uno u otro no es cosmético: el de hotel arranca con 2 adultos
// y el de ocio con 1, y eso es el numTicketsAdults de la URL.
import { modalConPlantilla } from './modales.js'
import {
  urlTrenHotel, urlViajePlus, urlParam, urlHotel, reglaInternacional, fechasDe, rutaCompraDestino,
  urlTrenHotelCoche, urlTrenCoche,
} from './salidas.js'
import { crearCalendarioHotel, crearCalendarioCoche } from './calendario-hotel.js'
import { mismoDia } from './fechas.js'

// El `_paramType` de cada bloque. Son los números que espera la landing del destino.
const TIPO = {
  bloqueTrenHotelActividad: '1648',
  bloquePaqueteTren: '1169',      // sólo en la rama de ocio
}

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {string} bloque
 */
export function crearBusqueda (buscador, bloque) {
  const boton = buscador.nodo(bloque, '.rvb-btn-buscar')
  if (!boton) return null

  const estado = buscador.estadoDe(bloque)

  /** Lo que hay que tener antes de poder buscar: las dos estaciones ELEGIDAS. */
  function listo () {
    return Boolean(estado.origen && estado.destino)
  }

  /**
   * La regla internacional corta la búsqueda antes de construir nada.
   * @returns {boolean} true si hay que parar
   */
  function corta () {
    const regla = reglaInternacional(buscador.datos, estado)
    if (!regla.corta) return false
    if (regla.aviso) {
      // La home usa un alert() del navegador, con el texto en los dos idiomas. Aquí va por el
      // modal propio: un alert() bloquea la pestaña entera de la landing anfitriona.
      buscador.avisar(regla.aviso)
      return true
    }
    buscador.irA(buscador.datos.url(regla.url))
    return true
  }

  function analiticaTren (tipoServicio) {
    const p = estado.pasajeros
    buscador.analiticaAdobe('clicBuscarBillete', {
      origen: estado.origen.nombre,
      destino: estado.destino.nombre,
      tipoViaje: estado.soloIda ? 'solo ida' : 'Ida y vuelta',
      nPasajeros: p ? p.adultos + p.ninos + p.bebes : 0,
      nAdultos: p ? p.adultos : 0,
      nNinos: p ? p.ninos : 0,
      nBebes: p ? p.bebes : 0,
      tipoServicio,
    })
  }

  // --- las tres salidas ---------------------------------------------------------------

  function salirTrenHotel (fechaSalidaHotel) {
    analiticaTren('Tren+Hotel')
    buscador.irA(urlTrenHotel(buscador.datos, estado, { fechaSalidaHotel }))
  }

  async function salirOcio () {
    analiticaTren('Tren+Ocio')
    // El paquete Viaje+ del destino decide la rama. Si la llamada falla, la home cae a la
    // forma _param…; si responde null, NO cae — sigue por CompraDestino. Son dos cosas
    // distintas y aquí se distinguen igual.
    let viajePlus
    try {
      viajePlus = await buscador.datos.viajePlusCrudo(estado.destino.id)
    } catch (e) {
      buscador.irA(urlParam(buscador.datos, estado, TIPO.bloquePaqueteTren))
      return
    }
    // A que proceso de compra de destino entra: `data-compra-destino="nuevo"` manda al
    // rediseno. Sin decir nada, a la de siempre.
    buscador.irA(urlViajePlus(buscador.datos, estado, viajePlus,
      rutaCompraDestino((buscador.opciones || {}).compraDestino)))
  }

  function salirParam () {
    analiticaTren('Tren+Ocio')
    buscador.irA(urlParam(buscador.datos, estado, TIPO[bloque] || '1648', {
      conHabitaciones: true,
      // BUG 2 de F0, replicado a propósito: en este bloque la home lee el número de
      // habitaciones de un selector que no existe, así que manda literalmente `undefined`.
      roomCount: undefined,
    }))
  }

  function salirTrenHotelCoche (fechaSalidaHotel) {
    analiticaTren('Tren+Hotel+Coche')
    buscador.irA(urlTrenHotelCoche(buscador.datos, estado, { fechaSalidaHotel }))
  }

  /** @param {{recogida: Date, devolucion: Date}} [fechasCoche]  las del calendario de sólo ida */
  function salirTrenCoche (fechasCoche) {
    analiticaTren('Tren+Coche')
    buscador.irA(urlTrenCoche(buscador.datos, estado, fechasCoche))
  }

  // Qué modal de pasajeros se abre y a dónde se sale, según la respuesta a la pregunta del
  // hotel. El del buscador de siempre, y el del tren + coche con sus dos modales (los mismos
  // que los de tren + hotel y tren + ocio, más la edad del conductor).
  const CAMINOS = buscador.producto === 'tren-coche'
    ? {
        conHotel: { plantilla: 'menuPasajerosTrenHotelCoche', salir: salirTrenHotelCoche },
        sinHotel: { plantilla: 'menuPasajerosTrenCoche', salir: salirTrenCoche },
      }
    : {
        conHotel: { plantilla: 'menuPasajerosTrenHotel', salir: salirTrenHotel },
        sinHotel: { plantilla: 'menuPasajerosTrenOcio', salir: salirOcio },
      }

  function salirHotel () {
    buscador.analiticaAdobe('clicBuscarBillete', {
      destino: estado.destino.nombre,
      ciudad: estado.destino.nombre,
      nPasajeros: estado.pasajeros ? estado.pasajeros.adultos + estado.pasajeros.ninos + estado.pasajeros.bebes : 0,
      nAdultos: estado.pasajeros ? estado.pasajeros.adultos : 0,
      nNinos: estado.pasajeros ? estado.pasajeros.ninos : 0,
      nBebes: estado.pasajeros ? estado.pasajeros.bebes : 0,
      tipoServicio: 'Hotel',
    })
    buscador.irA(urlHotel(buscador.datos, estado))
  }

  // --- la conversación ----------------------------------------------------------------

  /**
   * Ida y vuelta EL MISMO DÍA: se va y se vuelve, no hay noche que dormir.
   *
   * Preguntar ahí «¿quieres añadir hotel?» es ofrecer algo que no existe, y decir que sí llevaba
   * a un paquete de hotel de cero noches. Antes ni se podía elegir: el calendario avisaba y te
   * reseteaba las fechas a mañana y pasado. Ahora se deja elegir y lo que se hace es no preguntar
   * —se sale por la rama de siempre, la de sin hotel—. Donde el hotel es el producto
   * (`tren-hotel`, tren+hotel+ocio y el buscador de hotel) el calendario lo sigue impidiendo.
   */
  function idaYVueltaElMismoDia () {
    return !estado.soloIda
      && Boolean(estado.fechaIda) && Boolean(estado.fechaVuelta)
      && mismoDia(estado.fechaIda, estado.fechaVuelta)
  }

  /** El modal "¿quieres añadir hotel?", con los textos que trae el markup. */
  function preguntarHotel () {
    const modal = modalConPlantilla(buscador, 'swalHotel', 'rvb-swal-hotel')
    if (!modal) { pedirPasajeros(true); return }
    const si = modal.contenido.querySelector('.rvb-btn-confirm')
    const no = modal.contenido.querySelector('.rvb-btn-deny')
    if (si) si.addEventListener('click', (e) => { e.preventDefault(); modal.cerrar(); conHotel() })
    if (no) no.addEventListener('click', (e) => { e.preventDefault(); modal.cerrar(); sinHotel() })
  }

  /** Sí al hotel: si el viaje es sólo ida, antes hay que preguntar las noches. */
  function conHotel () {
    if (!estado.soloIda) { pedirPasajeros(true); return }
    crearCalendarioHotel(buscador, estado, (fechaSalida) => pedirPasajeros(true, fechaSalida))
  }

  /**
   * No al hotel. En el tren + coche de sólo ida no hay vuelta del tren que diga hasta cuándo se
   * quiere el coche, así que se pregunta; en ida y vuelta el coche va con los trenes.
   */
  function sinHotel () {
    if (buscador.producto === 'tren-coche' && estado.soloIda) {
      crearCalendarioCoche(buscador, estado, (recogida, devolucion) => pedirPasajeros(false, { recogida, devolucion }))
      return
    }
    pedirPasajeros(false)
  }

  /**
   * @param {boolean} conHotelElegido  decide el modal (2 adultos o 1) y la rama de salida
   * @param {Date|{recogida: Date, devolucion: Date}} [fechas]  la salida del hotel o, en el
   *                                   tren + coche sin hotel, la recogida y devolución del coche
   */
  function pedirPasajeros (conHotelElegido, fechas) {
    const pasajeros = buscador.pasajerosDe && buscador.pasajerosDe[bloque]
    if (!pasajeros) return
    const camino = conHotelElegido ? CAMINOS.conHotel : CAMINOS.sinHotel
    pasajeros.abrir({
      plantilla: camino.plantilla,
      alTerminar: () => camino.salir(fechas),
    })
  }

  // --- el botón -----------------------------------------------------------------------
  boton.addEventListener('click', (e) => {
    e.preventDefault()
    if (boton.disabled || !listo()) return
    if (corta()) return

    if (bloque === 'bloquePaqueteTren') {
      // El buscador de tren + hotel no pregunta: el cliente ya ha elegido hotel al abrirlo.
      if (buscador.producto === 'tren-hotel') conHotel()
      // Ida y vuelta en el día: tampoco se pregunta, porque no hay hotel que ofrecer.
      else if (idaYVueltaElMismoDia()) sinHotel()
      else preguntarHotel()
      return
    }
    if (bloque === 'bloqueHotel') { salirHotel(); return }
    salirParam()
  })

  return {
    // Para las pruebas: entrar en cada rama sin pasar por los modales.
    salirTrenHotel, salirOcio, salirParam, salirTrenHotelCoche, salirTrenCoche,
    idaYVueltaElMismoDia,
    // Lo usa el calendario cuando las fechas son el mismo día: ver `calendario.js`.
    continuarSinHotel: sinHotel,
    destruir () {},
  }
}
